import { useEffect, useState } from 'react';
import { X, Loader2, Send } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

interface Props {
  templateType: string;
  onClose: () => void;
}

const DUMMY = { first_name: 'Mischa', company_name: 'Voorbeeld B.V.' };

export function MailPreviewModal({ templateType, onClose }: Props) {
  const auth = useAuth() as any;
  const { showToast } = useToast();
  const [tmpl, setTmpl] = useState<{ subject: string; body: string; html_body: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('email_templates')
        .select('subject, body, html_body, template_type')
        .filter('template_type', 'eq', templateType)
        .limit(1);
      if (cancelled) return;
      if (error) {
        console.error('[MailPreviewModal] query error:', error, 'for templateType:', templateType);
      }
      const row = (data && data.length > 0) ? data[0] : null;
      setTmpl(row);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [templateType]);

  function render(s: string) {
    return s.replace(/\{\{first_name\}\}/g, DUMMY.first_name).replace(/\{\{company_name\}\}/g, DUMMY.company_name);
  }

  async function sendTestMail() {
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userEmail = (auth.user as any)?.email || 'info@uniekwerk.nl';
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token ?? ''}`,
        },
        body: JSON.stringify({
          to: userEmail,
          template_type: templateType,
          variables: {
            first_name: 'Mischa',
            company_name: 'Voorbeeld B.V.',
            step_title: 'Voorbeeld stap',
            step_key: 'voorbeeld_stap',
            user_email: 'voorbeeld@klant.nl',
            user_url: 'https://app.dibbi.nl',
            blog_title: 'Voorbeeld blog',
            blog_url: 'https://app.dibbi.nl/my-blogs',
          },
          system: true,
        }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      showToast('Test-mail verstuurd', 'success', `Bekijk je inbox op ${userEmail}`);
    } catch (e: any) {
      showToast('Test-mail mislukt', 'error', e.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h2 className="text-base font-semibold">Preview: <code>{templateType}</code></h2>
          <div className="flex items-center gap-2">
            <button
              onClick={sendTestMail}
              disabled={sending}
              className="px-2 py-1 text-xs bg-teal-600 hover:bg-teal-700 text-white rounded flex items-center gap-1 disabled:opacity-50"
              title="Stuur test-mail naar je eigen inbox"
            >
              <Send className="w-3 h-3" /> {sending ? 'Bezig...' : 'Stuur naar mij'}
            </button>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-5 overflow-y-auto">
          {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : tmpl ? (
            <>
              <div className="text-xs text-slate-500 mb-1">Onderwerp</div>
              <div className="text-base font-medium mb-4 text-slate-900">{render(tmpl.subject)}</div>
              <div className="text-xs text-slate-500 mb-1">Body (gerenderd met dummy-vars: {DUMMY.first_name} / {DUMMY.company_name})</div>
              <div className="bg-slate-50 rounded p-3 text-sm whitespace-pre-wrap border border-slate-200 text-slate-800">{render(tmpl.body)}</div>
            </>
          ) : <div className="text-slate-500">Template niet gevonden.</div>}
        </div>
      </div>
    </div>
  );
}
